/**
 * Step 9 Mobile: Strategy
 * Radio cards for decumulation approach, sliders for projection years.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { type Step8FormData, Step8Schema, type AllStepFormData, type AnnuityPurchaseData } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';
import { AnnuityPurchaseSection } from './AnnuityPurchaseSection';
import { isFeatureEnabled } from '../../config/features';
import { replaceFirstAnnuityPurchase, useAnnuityBlockedState } from './annuityPurchaseHelpers';

interface Step7Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step8FormData) => void;
  onSave: (data: Step8FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

const strategies: { value: Step8FormData['decumulationStrategy']; label: string; desc: string }[] = [
  { value: 'TAX_OPTIMISED', label: 'Tax-Optimised', desc: 'Minimise tax each year' },
  { value: 'ISA_FIRST', label: 'ISA First', desc: 'Draw ISA before SIPP' },
  { value: 'SIPP_FIRST', label: 'SIPP First', desc: 'Draw SIPP before ISA' },
  { value: 'PROPORTIONAL', label: 'Proportional', desc: 'Draw proportionally' },
];

export default function Step7StrategyMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step7Props>) {
  const { register, handleSubmit, watch, setValue, getValues } = useForm<Step8FormData>({
    resolver: zodResolver(Step8Schema),
    defaultValues: {
      decumulationStrategy: initialData?.decumulationStrategy || 'TAX_OPTIMISED',
      projectionYears: initialData?.projectionYears || 30,
      isaAnnualAllowance: initialData?.isaAnnualAllowance ?? 20000,
      isaShelteringMode: initialData?.isaShelteringMode || 'FULL',
      bandFillMode: initialData?.bandFillMode || 'PA_AND_BASIC',
      timeHorizonRebalancing: initialData?.timeHorizonRebalancing ?? false,
      // Omission, never null — the server schema rejects an explicit null.
      emergencyFund: initialData?.emergencyFund ?? undefined,
      annuityPurchases: initialData?.annuityPurchases ?? undefined,
    },
  });

  const handleBlur = () => onSave(watch());

  const strategy = watch('decumulationStrategy');
  const projYears = watch('projectionYears');

  // Annuity purchase — same section, ids and rate auto-fill as the desktop form.
  const persons = initialData?.persons ?? [];
  const annuityPersons = persons.length > 0 ? persons : [undefined];
  // A switch only stops NEW input: existing data keeps its inputs (see the desktop step).
  const showReserves = isFeatureEnabled('cashReserves') || (initialData?.emergencyFund ?? 0) > 0;
  const showAnnuity = isFeatureEnabled('annuityPurchase')
    || Object.values(initialData?.annuityPurchases ?? {}).some(rows => (rows?.length ?? 0) > 0);
  const isJointPlan = initialData?.planType === 'JOINT';
  const annuityPurchases = watch('annuityPurchases');
  // Same gate as the desktop twin — without it this step advanced with a
  // below-minimum age showing on screen and the default still in the draft.
  const { annuityBlocked, handleAnnuityValidity } = useAnnuityBlockedState();
  const setAnnuityPurchase = useCallback((personIndex: number, purchase: AnnuityPurchaseData | null) => {
    const current = getValues('annuityPurchases') ?? {};
    setValue('annuityPurchases', {
      ...current,
      // Preserve any purchases beyond the first — see the desktop twin.
      [`person${personIndex}`]: replaceFirstAnnuityPurchase(current[`person${personIndex}`], purchase),
    }, { shouldDirty: true });
    onSave(getValues());
  }, [getValues, setValue, onSave]);

  return (
    <WizardMobileShell step={9} totalSteps={10} title="Strategy" onBack={onBack} onContinue={() => handleSubmit(onNext)()} continueDisabled={annuityBlocked} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <fieldset className="mobile-fieldset">
          <legend className="mobile-label">Decumulation approach</legend>
          {strategies.map(s => (
            <label key={s.value} className={`mobile-radio-card${strategy === s.value ? ' mobile-radio-card--active' : ''}`}>
              <input type="radio" value={s.value} className="mobile-segment-input" {...register('decumulationStrategy')} onChange={e => { void register('decumulationStrategy').onChange(e); handleBlur(); }} />
              <span className="mobile-radio-card__title">{s.label}</span>
              <span className="mobile-radio-card__desc">{s.desc}</span>
            </label>
          ))}
        </fieldset>

        <label className="mobile-label">Projection years: {projYears}</label>
        <input type="range" className="mobile-slider" min={10} max={100} value={projYears} onChange={e => { setValue('projectionYears', parseInt(e.target.value, 10)); handleBlur(); }} />

        <label htmlFor="m-isa-allow" className="mobile-label">ISA annual allowance (£)</label>
        <input id="m-isa-allow" type="number" className="mobile-input" {...register('isaAnnualAllowance', { valueAsNumber: true })} onBlur={handleBlur} />

        {showReserves && (<>
        <label htmlFor="emergencyFund" className="mobile-label">Emergency fund to keep in cash (£, today&apos;s money)</label>
        <span className="mobile-hint">Kept in cash, not invested, and only used when everything else has run out</span>
        <input id="emergencyFund" type="number" min={0} className="mobile-input" {...register('emergencyFund', { valueAsNumber: true })} onBlur={handleBlur} />
        </>)}

        <label className="mobile-label">ISA sheltering</label>
        <div className="mobile-segmented-control">
          {['FULL', 'AEA_AWARE'].map(v => (
            <label key={v} className={`mobile-segment${watch('isaShelteringMode') === v ? ' mobile-segment--active' : ''}`}>
              <input type="radio" className="mobile-segment-input" value={v} {...register('isaShelteringMode')} onChange={e => { void register('isaShelteringMode').onChange(e); handleBlur(); }} />
              <span>{v === 'FULL' ? 'Full' : 'AEA-Aware ⓘ'}</span>
            </label>
          ))}
        </div>

        <label className="mobile-label">Tax band fill</label>
        <div className="mobile-segmented-control">
          {['PA_ONLY', 'PA_AND_BASIC', 'PA_AND_HIGHER'].map(v => (
            <label key={v} className={`mobile-segment${watch('bandFillMode') === v ? ' mobile-segment--active' : ''}`}>
              <input type="radio" className="mobile-segment-input" value={v} {...register('bandFillMode')} onChange={e => { void register('bandFillMode').onChange(e); handleBlur(); }} />
              <span>{v === 'PA_ONLY' ? 'PA Only' : v === 'PA_AND_BASIC' ? 'PA & Basic ⓘ' : 'PA & Higher'}</span>
            </label>
          ))}
        </div>

        {showAnnuity && (
        <div className="mobile-annuity-section">
          <span className="mobile-label">Annuity purchase</span>
          <p className="mobile-help-text">
            Swap part of the pension for a guaranteed income for life, keeping the rest in drawdown.
            Rates are indicative — get your own quote before deciding.
          </p>
          {annuityPersons.map((person, i) => (
            <AnnuityPurchaseSection
              key={person?.fullName || `annuity-person-${i}`}
              personIndex={i}
              person={person}
              showPersonName={annuityPersons.length > 1}
              isJointPlan={isJointPlan}
              purchase={annuityPurchases?.[`person${i}`]?.[0]}
              purchaseCount={annuityPurchases?.[`person${i}`]?.length}
              onChange={setAnnuityPurchase}
              onValidityChange={handleAnnuityValidity}
              variant="mobile"
            />
          ))}
        </div>
        )}

        <label className="mobile-label">Rebalancing</label>
        <div className="mobile-segmented-control">
          {[{ value: true, label: 'On' }, { value: false, label: 'Off' }].map(({ value, label }) => (
            <label key={label} className={`mobile-segment${watch('timeHorizonRebalancing') === value ? ' mobile-segment--active' : ''}`}>
              <input type="radio" className="mobile-segment-input" checked={watch('timeHorizonRebalancing') === value} onChange={() => { setValue('timeHorizonRebalancing', value); handleBlur(); }} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>
    </WizardMobileShell>
  );
}
